import React, { useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useQuery, useApolloClient } from "@apollo/client";
import ErrorScreen from "core/components/ErrorScreen";
import { ContentQuery, NextPlaylistContentQuery } from "arena/queries";
import ShortyVirtualItem from "./ShortyVirtualItem";
import ShortyPlaceholder from "./ShortyPlaceholder";
import ShortyLoadingSkeleton from "./ShortyLoadingSkeleton";
import { useSearchParams } from "react-router-dom";

export default function ShortyVirtualFeed({ initialContentId }) {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("productId");
  const collectionId = searchParams.get("collectionId");
  const client = useApolloClient();

  const [contents, setContents] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0); // Индекс активного элемента
  const [hasLoadedInitialNext, setHasLoadedInitialNext] = useState(false); // Флаг для первой загрузки следующего контента

  // Refs для debounce логики
  const scrollTimeoutRef = useRef(null);
  const pendingActiveIndexRef = useRef(0);

  // Параметры виртуализации
  // Динамический размер окна в зависимости от производительности устройства
  const isMobile =
    typeof window !== "undefined" ? window.innerWidth <= 768 : false;
  const RENDER_WINDOW_SIZE = isMobile ? 2 : 3; // Меньше элементов для мобильных устройств

  // Загружаем начальный контент
  const {
    data: initialData,
    loading: initialLoading,
    error,
  } = useQuery(ContentQuery, {
    variables: { contentId: initialContentId },
    onCompleted: (data) => {
      if (data.content) {
        setContents((prev) => {
          // Проверяем, есть ли уже этот контент в списке
          const existingIndex = prev.findIndex((c) => c.id === data.content.id);
          if (existingIndex !== -1) {
            // Если контент уже есть, просто обновляем его данные
            const updated = [...prev];
            updated[existingIndex] = data.content;
            return updated;
          }
          // Если контента нет, добавляем в начало
          return [data.content, ...prev];
        });
      }
    },
  });

  // Загружаем следующий контент для первого элемента (только один раз)
  useQuery(NextPlaylistContentQuery, {
    skip: !initialData?.content || hasLoadedInitialNext,
    fetchPolicy: "network-only",
    variables: {
      contentId: initialContentId,
      productId,
      collectionId,
      type: "SHORTY",
      contentIdsNot: contents.map((content) => content.id),
    },
    onCompleted: (data) => {
      // Помечаем что начали загрузку следующего контента
      setHasLoadedInitialNext(true);

      if (data.nextPlaylistContent) {
        setContents((prev) => {
          return [...prev, data.nextPlaylistContent];
        });
      }
    },
  });

  // Функция загрузки следующего контента
  const loadNextContent = useCallback(
    async (fromContentId) => {
      if (loadingMore) return;

      setLoadingMore(true);

      try {
        const { data } = await client.query({
          query: NextPlaylistContentQuery,
          variables: {
            contentId: fromContentId,
            productId,
            collectionId,
            type: "SHORTY",
            contentIdsNot: contents.map((content) => content.id),
          },
        });

        if (data?.nextPlaylistContent) {
          setContents((prev) => [...prev, data.nextPlaylistContent]);
        }
      } catch (error) {
        console.error("Error loading next content:", error);
      } finally {
        setLoadingMore(false);
      }
    },
    [loadingMore, client, productId, collectionId, contents]
  );

  // Callback для загрузки больше контента
  const handleLoadMore = useCallback(() => {
    if (contents.length === 0) return;

    const lastContent = contents[contents.length - 1];
    loadNextContent(lastContent.id);
  }, [contents, loadNextContent]);

  // Debounced версия обновления activeIndex для оптимизации при быстрой прокрутке
  const handleActivateDebounced = useCallback(
    (index) => {
      pendingActiveIndexRef.current = index;

      // Очищаем предыдущий таймаут
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Устанавливаем новый таймаут
      scrollTimeoutRef.current = setTimeout(() => {
        const newActiveIndex = pendingActiveIndexRef.current;
        setActiveIndex(newActiveIndex);

        // Проверяем нужно ли загрузить больше контента при прокрутке
        // Загружаем если переходим к предпоследнему или последнему элементу
        if (newActiveIndex >= contents.length - 2 && !loadingMore) {
          handleLoadMore();
        }
      }, 150); // Задержка 150мс для избежания частых обновлений
    },
    [contents.length, loadingMore, handleLoadMore]
  );

  // Навигация между видео
  const handleNavigate = useCallback(
    (targetIndex) => {
      if (targetIndex >= 0 && targetIndex < contents.length) {
        setActiveIndex(targetIndex);

        // Прокручиваем к целевому элементу для полноэкранного режима
        if (typeof document !== "undefined") {
          // Небольшая задержка для обеспечения рендеринга
          setTimeout(() => {
            const container = document.querySelector("[data-shorty-feed]");
            if (container) {
              const targetElement = container.children[targetIndex];

              if (targetElement) {
                // Используем прямой скролл контейнера вместо scrollIntoView
                const elementTop = targetElement.offsetTop;
                container.scrollTo({
                  top: elementTop,
                  behavior: "smooth",
                });
              }
            }
          }, 100); // Небольшая задержка для обеспечения правильного рендеринга
        }

        // Проверяем нужно ли загрузить больше контента при навигации через кнопки
        // Загружаем если переходим к предпоследнему или последнему элементу
        if (targetIndex >= contents.length - 2 && !loadingMore) {
          handleLoadMore();
        }
      }
    },
    [contents.length, loadingMore, handleLoadMore]
  );

  // Очистка таймаута при размонтировании
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (initialLoading) {
    return <ShortyLoadingSkeleton count={2} />;
  }

  // Вычисляем диапазон элементов для рендеринга
  const renderStartIndex = Math.max(0, activeIndex - RENDER_WINDOW_SIZE);
  const renderEndIndex = Math.min(
    contents.length - 1,
    activeIndex + RENDER_WINDOW_SIZE
  );

  return (
    <Box
      data-shorty-feed
      sx={{
        height: isMobileView
          ? "100dvh" // Dynamic viewport height для мобильных - исключает панельки браузера
          : "calc(100vh - 64px)", // На десктопе обычный vh минус header
        width: "100%",
        overflowY: "auto",
        scrollSnapType: "y mandatory",
        scrollBehavior: "smooth",
        position: "relative",
      }}
    >
      {contents.map((content, index) => {
        // Проверяем, находится ли элемент в окне рендеринга
        const isInRenderWindow =
          index >= renderStartIndex && index <= renderEndIndex;

        // Для элементов вне окна рендеринга показываем легковесный placeholder
        if (!isInRenderWindow) {
          return <ShortyPlaceholder key={`${content.id}-${index}`} />;
        }

        // Определяем нужно ли предзагружать элемент (следующий или предыдущий)
        const shouldPreload =
          (index === activeIndex + 1 && index < contents.length) || // следующий
          (index === activeIndex - 1 && index >= 0); // предыдущий

        // Для элементов в окне рендеринга показываем полный компонент
        return (
          <ShortyVirtualItem
            key={`${content.id}-${index}`}
            content={content}
            productId={productId}
            collectionId={collectionId}
            index={index}
            isInitial={content.id === initialContentId}
            isActive={index === activeIndex}
            shouldPreload={shouldPreload}
            onActivate={() => handleActivateDebounced(index)}
            onLoadMore={null}
            onNavigate={handleNavigate}
            totalItems={contents.length}
          />
        );
      })}
    </Box>
  );
}

ShortyVirtualFeed.propTypes = {
  initialContentId: PropTypes.string,
};
